import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { login, getSetting } from '@/api';
import { setToken } from '@/api/client';
import AgreementModal from '@/components/feature/AgreementModal';

//客服信息兜底默认（后台平台配置 service_phone/service_wechat/service_time 未配置时使用）
const SERVICE_INFO = {
  phone: '400-888-8888',
  wechat: 'zhongtaicheng666',
  time: '周一至周日 9:00-21:00',
};

export default function Login() {
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [toast, setToast] = useState('');
  const [modal, setModal] = useState<'service' | 'privacy' | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showService, setShowService] = useState(false); //忘记密码弹层：联系客服
  const [serviceInfo, setServiceInfo] = useState(SERVICE_INFO);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2000);
  };

  const copyText = (text: string) => {
    navigator.clipboard.writeText(text).catch(() => {});
    showToast('已复制');
  };

  /** 打开客服弹层：拉取后台平台配置的客服信息（读取失败保持默认） */
  const openService = () => {
    setShowService(true);
    getSetting('platform_config')
      .then((res) => {
        const s = (res.setting || {}) as unknown as Record<string, string>;
        setServiceInfo({
          phone: s.service_phone || SERVICE_INFO.phone,
          wechat: s.service_wechat || SERVICE_INFO.wechat,
          time: s.service_time || SERVICE_INFO.time,
        });
      })
      .catch(() => {});
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone.trim() || !password.trim()) {
      setToast('请输入手机号和密码');
      setTimeout(() => setToast(''), 2000);
      return;
    }
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await login(phone.trim(), password);
      setToken(res.token);
      setToast('登录成功');
      setTimeout(() => {
        setToast('');
        navigate('/');
      }, 800);
    } catch (err) {
      setToast(err instanceof Error ? err.message : '登录失败');
      setTimeout(() => setToast(''), 2000);
    } finally {
      setSubmitting(false);
    }
  };

  const closeModal = () => setModal(null);

  return (
    <div className="mobile-container bg-white min-h-screen flex flex-col relative">
      {/* Top bar */}
      <div className="flex items-center px-4 h-12">
        <div className="w-8 h-8" />
      </div>

      {/* Logo area */}
      <div className="flex flex-col items-center mt-6 mb-10">
        <div className="relative">
          {/* Soft glow behind */}
          <div className="absolute inset-0 bg-primary-400/25 rounded-3xl blur-xl scale-125" />
          <div className="relative w-16 h-16 rounded-2xl bg-primary-500 flex items-center justify-center">
            <span className="text-white text-2xl font-bold tracking-wider">众</span>
          </div>
        </div>
        <h1 className="mt-5 text-foreground-950 text-xl font-bold tracking-wide">众泰成</h1>
        <p className="mt-1.5 text-foreground-400 text-sm tracking-wider">私域寄卖 · 限时竞拍 · 裂变分销</p>
      </div>

      {/* Login form */}
      <form onSubmit={handleLogin} className="px-6 flex-1">
        <h2 className="text-foreground-950 text-lg font-bold mb-6">欢迎回来</h2>

        {/* Phone input */}
        <div className="relative mb-4">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center justify-center text-foreground-400">
            <i className="ri-smartphone-line text-lg" />
          </div>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="请输入手机号"
            maxLength={11}
            className="w-full h-12 pl-12 pr-4 rounded-xl bg-white border border-background-300 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 transition-all"
          />
        </div>

        {/* Password input */}
        <div className="relative mb-6">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center justify-center text-foreground-400">
            <i className="ri-shield-keyhole-line text-lg" />
          </div>
          <input
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="请输入登录密码"
            className="w-full h-12 pl-12 pr-12 rounded-xl bg-white border border-background-300 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 transition-all"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center justify-center text-foreground-400 cursor-pointer hover:text-foreground-600 transition-colors"
            aria-label={showPassword ? '隐藏密码' : '显示密码'}
          >
            <i className={`${showPassword ? 'ri-eye-line' : 'ri-eye-off-line'} text-lg`} />
          </button>
        </div>

        {/* Submit button */}
        <button
          type="submit"
          disabled={submitting}
          className="w-full h-12 rounded-xl bg-primary-500 text-white text-sm font-bold flex items-center justify-center gap-2 cursor-pointer hover:bg-primary-600 active:scale-[0.98] transition-all shadow-lg shadow-primary-500/20 disabled:opacity-60"
        >
          {submitting ? '登录中...' : '立即登录'}
          <i className="ri-arrow-right-line text-sm" />
        </button>

        {/* Forgot password：弹层展示客服信息 */}
        <div className="flex justify-end mt-4">
          <button
            type="button"
            onClick={openService}
            className="text-foreground-400 text-sm cursor-pointer hover:text-primary-500 transition-colors"
          >
            忘记密码?
          </button>
        </div>

        {/* Register link */}
        <div className="flex items-center justify-center mt-8 gap-2">
          <span className="text-foreground-400 text-sm">还没有账号?</span>
          <button
            type="button"
            onClick={() => navigate('/register')}
            className="text-primary-500 text-sm font-bold cursor-pointer hover:text-primary-600 transition-colors"
          >
            立即注册
          </button>
        </div>
      </form>

      {/* Bottom agreement */}
      <div className="px-6 pb-8 pt-4 mt-auto">
        <p className="text-center text-foreground-400 text-xs leading-relaxed">
          登录即表示同意
          <button
            type="button"
            onClick={() => setModal('service')}
            className="text-primary-500 cursor-pointer hover:underline mx-0.5"
          >
            《用户服务协议》
          </button>
          和
          <button
            type="button"
            onClick={() => setModal('privacy')}
            className="text-primary-500 cursor-pointer hover:underline mx-0.5"
          >
            《隐私政策》
          </button>
        </p>
      </div>

      {/* 协议/隐私政策弹层 */}
      <AgreementModal type={modal} onClose={closeModal} onError={showToast} />

      {/* 忘记密码弹层：联系客服 */}
      {showService && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setShowService(false)}
        >
          <div className="w-full max-w-sm bg-white rounded-2xl p-5" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-xl bg-primary-50 flex items-center justify-center">
                <i className="ri-customer-service-2-line text-primary-500 text-lg" />
              </div>
              <h3 className="text-foreground-900 text-base font-bold">联系客服找回密码</h3>
            </div>

            <p className="text-foreground-400 text-xs leading-relaxed mb-4">
              为保障账户安全，请联系客服协助找回密码
            </p>

            {/* 客服信息 */}
            <div className="rounded-xl bg-background-50 border border-background-200/40 px-4 py-1 mb-4">
              <div className="flex items-center justify-between py-2.5">
                <span className="text-foreground-500 text-xs">客服电话</span>
                <span className="flex items-center gap-1.5">
                  <span className="text-foreground-800 text-sm font-bold">{serviceInfo.phone}</span>
                  <button
                    onClick={() => copyText(serviceInfo.phone)}
                    className="text-primary-500 text-xs cursor-pointer hover:text-primary-600 active:scale-95 transition-all"
                    aria-label="复制客服电话"
                  >
                    <i className="ri-file-copy-line" />
                  </button>
                </span>
              </div>
              <div className="border-t border-dashed border-background-200" />
              <div className="flex items-center justify-between py-2.5">
                <span className="text-foreground-500 text-xs">客服微信</span>
                <span className="flex items-center gap-1.5">
                  <span className="text-foreground-800 text-sm font-bold">{serviceInfo.wechat}</span>
                  <button
                    onClick={() => copyText(serviceInfo.wechat)}
                    className="text-primary-500 text-xs cursor-pointer hover:text-primary-600 active:scale-95 transition-all"
                    aria-label="复制客服微信"
                  >
                    <i className="ri-file-copy-line" />
                  </button>
                </span>
              </div>
              <div className="border-t border-dashed border-background-200" />
              <div className="flex items-center justify-between py-2.5">
                <span className="text-foreground-500 text-xs">服务时间</span>
                <span className="text-foreground-800 text-sm font-bold">{serviceInfo.time}</span>
              </div>
            </div>

            {/* 操作按钮 */}
            <button
              onClick={() => setShowService(false)}
              className="w-full py-2.5 rounded-xl bg-primary-500 text-white text-sm font-bold cursor-pointer hover:bg-primary-600 active:scale-[0.98] transition-all"
            >
              我知道了
            </button>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 px-5 py-2.5 bg-black/70 backdrop-blur-sm rounded-2xl text-white text-sm z-50 shadow-xl">
          {toast}
        </div>
      )}
    </div>
  );
}